import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { logger } from '@services/logger/logger.service';
import { awaitOfficeReady, isOfficeJsLoaded, IOfficeReadyInfo } from '@core/core-boot/office-ready';
import { Core } from '@core/core';
import type { OfficeService } from '@core/office/office-service/office-service';
import { environment } from '@env';

const OFFICE_READY_TIMEOUT_MS = 120_000;
const OFFICE_JS_LOAD_WAIT_MS  = 5_000;
const MAX_BOOT_RETRIES        = 24;
const BOOT_RETRY_KEY          = 'r2:boot-retry-count';

function getRetryCount(): number
{
    const raw = sessionStorage.getItem(BOOT_RETRY_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

function scheduleReload(reason: string): void
{
    const attempts = getRetryCount();
    if (attempts >= MAX_BOOT_RETRIES)
    {
        logger.error(`[boot] giving up after ${ attempts } retries: ${ reason }`);
        return;
    }

    const next = attempts + 1;
    sessionStorage.setItem(BOOT_RETRY_KEY, String(next));

    const delay = Math.min(1_000 * Math.pow(2, attempts), 8_000);
    logger.warn(`[boot] retry ${ next }/${ MAX_BOOT_RETRIES } in ${ delay }ms: ${ reason }`);

    setTimeout(() => window.location.reload(), delay);
}

function waitForOfficeJs(timeoutMs: number): Promise<void>
{
    if (isOfficeJsLoaded())
    {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) =>
    {
        const start = Date.now();
        const intervalId = setInterval(() =>
        {
            if (isOfficeJsLoaded())
            {
                clearInterval(intervalId);
                resolve();
                return;
            }
            if (Date.now() - start >= timeoutMs)
            {
                clearInterval(intervalId);
                reject(new Error(`office.js did not become available within ${ timeoutMs }ms`));
            }
        }, 100);
    });
}

async function start(): Promise<void>
{
    try
    {
        await waitForOfficeJs(OFFICE_JS_LOAD_WAIT_MS);
    }
    catch (err)
    {
        scheduleReload(`office.js global missing: ${ String(err) }`);
        return;
    }

    let info: IOfficeReadyInfo;
    try
    {
        info = await awaitOfficeReady(OFFICE_READY_TIMEOUT_MS);
    }
    catch (err)
    {
        scheduleReload(`Office.onReady failed: ${ String(err) }`);
        return;
    }

    if (!info.platform || !info.host)
    {
        // Auth pages (/auth/*) run inside Office dialogs where host/platform may be null.
        // Allow them to bootstrap so MSAL sign-in can complete; the taskpane polls for session.
        if (!window.location.pathname.startsWith('/auth/'))
        {
            window.location.replace('no-office.html');
            return;
        }
    }

    sessionStorage.removeItem(BOOT_RETRY_KEY);
    logger.info('[boot] Office.onReady resolved', info);

    if (info.host === Office.HostType.Excel)
    {
        try
        {
            (window as any).setForceRefreshOn?.();
        }
        catch (err)
        {
            logger.warn('[boot] setForceRefreshOn call failed', err);
        }
    }

    bootstrapApplication(AppComponent, appConfig).then(async (app) =>
    {
        if (info.host === Office.HostType.Outlook && environment.print_logs
            && (environment.type === 'development' || environment.type === 'stage'))
        {
            const { probeOutlookGroup } = await import('@core/shared/outlook-graph-probe');
            (window as Window & { r2OutlookGroupProbe?: () => ReturnType<typeof probeOutlookGroup> }).r2OutlookGroupProbe = () =>
                probeOutlookGroup((app.injector.get(Core).office as OfficeService).getHost() as import('@core/shared/office-hosts/outlook-host').OutlookHost,
                    Office.context.mailbox?.userProfile?.emailAddress ?? '');
        }
    }).catch((err) => logger.error(err));
}

start().catch((err) => logger.error(err));
